/**
 * Filtros da pesquisa de Pagamento Individual.
 *
 * Os campos de lookup de empresa e turma guardam o `id` selecionado e o rótulo
 * exibido: o `id` vai para a query, o rótulo mantém o texto no autocomplete após
 * a seleção. O de aluno é a exceção — ver `studentLabel`.
 *
 * Todos os campos são `string` — inclusive os numéricos — para que o rascunho
 * espelhe o valor dos inputs sem conversões intermediárias. A conversão para os
 * query params acontece em `buildPeopleQueryParams`.
 */
export interface IndividualPaymentFilters {
  /**
   * Id numérico do aluno (mapeado para `PersonIds`), extraído da chave do lookup
   * com `parseStudentKeyId` — `person/shared/people-multiple` devolve
   * `"USR260953"`, e é o prefixo `USR` que precisa sair antes do uso na query.
   */
  readonly personId: string;
  /**
   * Rótulo do aluno selecionado no lookup (`"Nome  [email]"`), ou `''` quando
   * não há seleção. Serve para exibir o texto no campo e como marca de seleção
   * ativa — não vai na query.
   */
  readonly studentLabel: string;
  /**
   * Nome do aluno enviado em `StudentName` — vem da seleção no lookup ou do
   * texto digitado sem seleção.
   */
  readonly studentName: string;
  /**
   * E-mail do aluno extraído do rótulo do lookup, enviado em `Email` (filtro
   * exato). `''` quando o rótulo não traz e-mail ou não há seleção.
   */
  readonly studentEmail: string;
  /** `key` do lookup de empresa (mapeado para `CompanyId`). */
  readonly companyId: string;
  readonly companyLabel: string;
  /** `key` do lookup de turma (mapeado para `ClassStudentId`). */
  readonly classStudentId: string;
  readonly classStudentLabel: string;
  /** `key` do método de pagamento (`EPaymentMethod`); `''` significa "Todos". */
  readonly paymentMethod: string;
  readonly ra: string;
  readonly cpf: string;
  readonly email: string;
  readonly thirdPartyCpf: string;
  readonly thirdPartyCnpj: string;
  readonly ourNumber: string;
  readonly invoiceNumber: string;
}

/**
 * Campos que vivem no painel "Filtros avançados".
 *
 * Usado para contar filtros avançados ativos e para limpar somente o painel,
 * preservando Aluno, Empresa, Turma e Método de Pagamento.
 */
export const ADVANCED_FILTER_KEYS = [
  'ra',
  'cpf',
  'email',
  'thirdPartyCpf',
  'thirdPartyCnpj',
  'ourNumber',
  'invoiceNumber',
] as const satisfies readonly (keyof IndividualPaymentFilters)[];

export type AdvancedFilterKey = (typeof ADVANCED_FILTER_KEYS)[number];

/**
 * Filtros de cobrança aceitos também por
 * `GET /individualpayment/people/{personId}/courses`.
 *
 * Precisam ser repassados às linhas-filhas, senão a subtabela exibe contratos
 * que não casam com a pesquisa feita na linha-pai.
 */
export const CONTRACT_PASSTHROUGH_KEYS = [
  'ourNumber',
  'invoiceNumber',
  'thirdPartyCpf',
  'thirdPartyCnpj',
] as const satisfies readonly (keyof IndividualPaymentFilters)[];

/**
 * Ações do menu "Gerenciar" de uma linha-filha (contrato financeiro), na ordem
 * em que aparecem no dropdown.
 *
 * Todas operam sobre o mesmo `financialPersonId` (o `id` do contrato) — é a
 * ação escolhida que decide o destino, por isso o callback recebe as duas
 * informações juntas.
 */
export const CONTRACT_ACTIONS = [
  'paymentConsolidation',
  'editIndividualPayment',
  'viewParcels',
  'deactivateFinancial',
  'addIndividualPayment',
] as const;

export type IndividualPaymentContractAction = (typeof CONTRACT_ACTIONS)[number];

/**
 * `ECourseFinancialPersonStatus.Inactive` — status do contrato financeiro já
 * inativado.
 *
 * Só o valor que a tela consulta, não o enum inteiro: **não existe enum gerado
 * para este status** — nem o spec o descreve em prosa, como faz com os enums de
 * parcela. O valor vem do mapeamento do sistema legado, onde a mesma constante
 * esconde o botão de inativar.
 *
 * **O campo numérico tem nome diferente em cada ViewModel**: é `statusId` em
 * `IndividualPaymentPersonCourseViewModel` (a linha da listagem) e `status` em
 * `IndividualPaymentHeaderViewModel` (o cabeçalho da tela de parcelas). Nos dois
 * o irmão textual existe e serve só para exibir.
 */
export const FINANCIAL_PERSON_STATUS_INACTIVE = 4;
